import { useCallback, useRef, useState } from 'react';
import type { FormApi } from 'final-form';

import {
  useDriveTriggerStreamingPromptMutation,
  DriveConversationFragmentDoc,
} from '@occc/graphql';

import type { UseDriveChatOptions, UseDriveChatReturn } from './use-drive-chat.types';

import {
  useStreamOrchestrator,
  type StreamError,
  type ParsedContent,
  type WaitingStatus,
} from '../features/streaming';
import type { ChatMessage } from '../types';
import { createPromptMessage, createPendingResponseMessage } from '../utils/message-utils';
import {
  createErrorMessage,
  createNoDataErrorMessage,
  createGraphQLResultErrorMessage,
  createStalledErrorMessage,
} from '../utils/error-utils';
import {
  findStreamingMessage,
  isResumableStreamingResponse,
  updateMessageById,
} from '../utils/message-update-utils';
import { DEFAULT_STREAMING_STATUS_TEXT } from '../../drive-insights.constants';
import { parseContentSafely } from '../utils/content-parsing-utils';

const WAITING_STATUS_LABELS: Record<WaitingStatus, string> = {
  working: DEFAULT_STREAMING_STATUS_TEXT,
  complex: 'This is a complex query — processing…',
};

export const useDriveChat = ({
  conversationId,
  onConversationCreated,
}: UseDriveChatOptions): UseDriveChatReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [processingMessage, setProcessingMessage] = useState<string | null>(null);
  const formRef = useRef<FormApi<{ question: string }> | null>(null);

  const activeResponseIdRef = useRef<string | null>(null);
  const pendingResponseIdRef = useRef<string | null>(null);

  const handleContentUpdate = useCallback((text: string, blocks: ParsedContent[]) => {
    const responseId = activeResponseIdRef.current;
    if (!responseId) return;

    setProcessingMessage(null);

    const parsedContent = blocks.length > 0 ? blocks : parseContentSafely(text);

    setMessages((prev) => {
      const messageToUpdate = findStreamingMessage(prev, responseId);
      if (!messageToUpdate) return prev;

      const updates: Partial<ChatMessage> = {
        content: text,
        parsedContent,
        isStreaming: true,
      };

      return updateMessageById(prev, messageToUpdate.id, updates);
    });
  }, []);

  const handleStreamComplete = useCallback((text: string, blocks: ParsedContent[]) => {
    const responseId = activeResponseIdRef.current;
    if (!responseId) return;

    setProcessingMessage(null);

    const parsedContent = blocks.length > 0 ? blocks : parseContentSafely(text);

    setMessages((prev) => {
      const messageToUpdate = findStreamingMessage(prev, responseId);
      if (!messageToUpdate) return prev;

      const updates: Partial<ChatMessage> = {
        content: text,
        parsedContent,
        isStreaming: false,
        isComplete: true,
      };

      // Stamp unconditionally: thinking is over by definition when the stream ends,
      // even if no non-thinking content was received (unlike handleContentUpdate
      // which only stamps on the first non-thinking block).
      if (!messageToUpdate.thinkingEndedAt) {
        updates.thinkingEndedAt = Date.now();
      }

      return updateMessageById(prev, messageToUpdate.id, updates);
    });

    activeResponseIdRef.current = null;
  }, []);

  const handleWaiting = useCallback((status: WaitingStatus) => {
    setProcessingMessage(WAITING_STATUS_LABELS[status]);
  }, []);

  const handleStreamError = useCallback(
    (error: StreamError) => {
      setProcessingMessage(null);

      const responseId = activeResponseIdRef.current;
      const promptId = pendingResponseIdRef.current || `${Date.now()}-prompt`;

      setMessages((prev) => {
        const filtered = prev.filter(
          (msg) => msg.id !== responseId && msg.id !== pendingResponseIdRef.current,
        );
        const errorMessage =
          error.type === 'stalled'
            ? createStalledErrorMessage(promptId, conversationId)
            : createErrorMessage(new Error(error.message), promptId, conversationId);
        return [...filtered, errorMessage];
      });

      activeResponseIdRef.current = null;
      pendingResponseIdRef.current = null;
    },
    [conversationId],
  );

  const { startStream, stopStream, isStreaming } = useStreamOrchestrator({
    onContentUpdate: handleContentUpdate,
    onStreamComplete: handleStreamComplete,
    onError: handleStreamError,
    onWaiting: handleWaiting,
  });

  const [triggerStreamingPrompt, { loading: isMutationLoading }] =
    useDriveTriggerStreamingPromptMutation({
      onError: () => {
        // Suppress default toast - errors shown inline
      },
    });

  const submitPrompt = useCallback(
    async (prompt: string) => {
      const trimmedPrompt = prompt.trim();
      if (!trimmedPrompt || isMutationLoading || isStreaming) return;

      setProcessingMessage(null);

      const promptMessage = createPromptMessage(trimmedPrompt, conversationId);
      const pendingResponse = createPendingResponseMessage(conversationId);

      pendingResponseIdRef.current = pendingResponse.id;

      setMessages((prev) => [...prev, promptMessage, pendingResponse]);

      if (formRef.current) {
        formRef.current.reset();
      }

      let result;
      let caughtError: unknown = null;

      try {
        result = await triggerStreamingPrompt({
          variables: {
            input: {
              message: trimmedPrompt,
              conversationId,
            },
          },
          update(cache, { data }) {
            if (conversationId || !data?.driveTriggerStreamingPrompt) return;

            const res = data.driveTriggerStreamingPrompt;

            const newConversationRef = cache.writeFragment({
              fragment: DriveConversationFragmentDoc,
              fragmentName: 'DriveConversation',
              data: {
                __typename: 'DriveConversation',
                id: res.conversationId,
                createdAt: res.createdAt,
                prompts: [
                  {
                    __typename: 'DrivePrompt',
                    id: res.promptId,
                    content: trimmedPrompt,
                    conversationId: res.conversationId,
                    createdAt: res.createdAt,
                  },
                ],
                responses: [res],
              },
            });

            cache.modify({
              fields: {
                driveConversations(existing = {}, { storeFieldName }) {
                  const newEdge = {
                    __typename: 'DriveConversationEdge',
                    cursor: '',
                    node: newConversationRef,
                  };
                  const allEdges = [newEdge, ...(existing.edges ?? [])];

                  // Respect the `first` limit from the original query variables
                  // so the cache doesn't grow beyond what was requested.
                  const firstMatch = storeFieldName.match(/"first":(\d+)/);
                  const limit = firstMatch ? Number(firstMatch[1]) : allEdges.length;

                  return {
                    ...existing,
                    edges: allEdges.slice(0, limit),
                  };
                },
              },
            });
          },
        });
      } catch (error) {
        caughtError = error;
      }

      if (caughtError) {
        const errorMessage = createErrorMessage(caughtError, promptMessage.id, conversationId);
        setMessages((prev) =>
          prev.filter((msg) => msg.id !== pendingResponse.id).concat(errorMessage),
        );
        pendingResponseIdRef.current = null;
        return;
      }

      if (result?.errors && result.errors.length > 0) {
        const errorMessage = createGraphQLResultErrorMessage(
          result.errors,
          promptMessage.id,
          conversationId,
        );
        setMessages((prev) =>
          prev.filter((msg) => msg.id !== pendingResponse.id).concat(errorMessage),
        );
        pendingResponseIdRef.current = null;
        return;
      }

      if (!result?.data?.driveTriggerStreamingPrompt) {
        const errorMessage = createNoDataErrorMessage(promptMessage.id, conversationId);
        setMessages((prev) =>
          prev.filter((msg) => msg.id !== pendingResponse.id).concat(errorMessage),
        );
        pendingResponseIdRef.current = null;
        return;
      }

      const response = result.data.driveTriggerStreamingPrompt;

      activeResponseIdRef.current = response.id;

      startStream(response.conversationId, response.id);

      if (!conversationId || conversationId !== response.conversationId) {
        onConversationCreated(response.conversationId);
      }

      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id === pendingResponse.id) {
            return {
              ...msg,
              id: response.id,
              conversationId: response.conversationId,
              promptId: response.promptId,
            };
          }
          return msg;
        }),
      );

      pendingResponseIdRef.current = null;

      if (response.complete && response.content) {
        const parsedContent = parseContentSafely(response.content);

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id === response.id) {
              return {
                ...msg,
                content: response.content || '',
                parsedContent,
                isStreaming: false,
                isComplete: true,
              };
            }
            return msg;
          }),
        );

        stopStream();
        setProcessingMessage(null);
        activeResponseIdRef.current = null;
      }
    },
    [
      conversationId,
      isMutationLoading,
      isStreaming,
      triggerStreamingPrompt,
      onConversationCreated,
      startStream,
      stopStream,
    ],
  );

  const loadMessages = useCallback(
    (newMessages: ChatMessage[]) => {
      stopStream();
      setProcessingMessage(null);
      setMessages(newMessages);

      const lastMessage = newMessages[newMessages.length - 1];
      if (isResumableStreamingResponse(lastMessage)) {
        activeResponseIdRef.current = lastMessage.id;
        startStream(lastMessage.conversationId, lastMessage.id, {
          content: lastMessage.content,
          lastChunkNumber: lastMessage.lastChunkNumber,
        });
      }
    },
    [stopStream, startStream],
  );

  return {
    messages,
    submitPrompt,
    isStreaming,
    processingMessage,
    formRef,
    setMessages,
    loadMessages,
  };
};
